'use client';

import type { IntentHandlers } from '@morfi/backend-sdk-v2/client';
import type { PointerEventHandler, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NetworkLink } from '@/i18n/network-link';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// NavLink — reusable navigation-style link primitive
// ---------------------------------------------------------------------------

const NAV_LINK_BASE_CLASSNAME =
  'inline-flex items-center whitespace-nowrap text-body font-medium touch-target transition-colors duration-fast ease-default motion-reduce:transition-none';

interface NavLinkProps {
  href: string;
  active?: boolean;
  flash?: boolean;
  children: ReactNode;
  className?: string;
  /**
   * Hover/focus/touch intent — typically the spread of
   * `useIntentPrefetch(...)` from `@morfi/backend-sdk-v2/client`. Wires
   * predictive prefetch on the nav link.
   *
   * Prefer passing `intent={...}` over the discrete handler props — the
   * typed bag tracks the SDK's `IntentHandlers` contract so any future
   * intent surface lands on every consumer at once. The discrete props
   * remain available (and win when both are supplied) for callers that
   * want per-event handlers.
   */
  intent?: IntentHandlers;
  onMouseEnter?: () => void;
  onFocus?: () => void;
  onPointerDown?: () => void;
  /**
   * Next.js `<Link prefetch>`. Defaults to `true` (full-RSC prefetch). It MUST
   * be present from the initial render — a runtime-toggled `prefetch` only
   * triggers an AUTO/shell prefetch (the route loading.tsx, not the data), so
   * the click still flashes a skeleton. With a literal `true` from mount, Next
   * full-prefetches the destination RSC (incl. the SSR-dehydrated SDK cache) on
   * viewport-enter — the nav anchors are always on-screen, so the data is warm
   * ahead of the click → zero loading state on both desktop and touch. Pass
   * `prefetch={false}` only for a non-data nav target.
   */
  prefetch?: boolean;
}

function NavLink({
  href,
  active = false,
  flash = false,
  children,
  className,
  intent,
  onMouseEnter,
  onFocus,
  onPointerDown,
  prefetch,
}: NavLinkProps): React.ReactNode {
  // `intent` / discrete handlers still wire any ADDITIVE on-hover data warming
  // (e.g. category-nav warming a FILTERED feed) — separate from the route's own
  // full-RSC prefetch, which is the literal `prefetch` below.
  return (
    <NetworkLink
      href={href}
      prefetch={prefetch ?? true}
      aria-current={active ? 'page' : undefined}
      onMouseEnter={onMouseEnter ?? intent?.onMouseEnter}
      onFocus={onFocus ?? intent?.onFocus}
      onPointerDown={onPointerDown ?? intent?.onPointerDown}
      className={cn(
        NAV_LINK_BASE_CLASSNAME,
        flash
          ? 'text-foreground-brand'
          : active
            ? 'text-foreground-primary'
            : 'text-foreground-tertiary hover:text-foreground-primary',
        className,
      )}
    >
      {children}
    </NetworkLink>
  );
}

// ---------------------------------------------------------------------------
// NavigationListItem — alias of NavLink kept for legacy call sites
// ---------------------------------------------------------------------------

type NavigationListItemProps = NavLinkProps;

function NavigationListItem(props: NavigationListItemProps): React.ReactNode {
  return <NavLink {...props} />;
}

// ---------------------------------------------------------------------------
// NavigationList
// ---------------------------------------------------------------------------

interface NavigationListProps {
  children: ReactNode;
  className?: string;
  /** Classes for the inner scroll container (the `<nav>`). Use for padding that
   *  must scroll WITH the items — e.g. a mobile edge-to-edge rail that bleeds out
   *  of the page gutter but keeps its first item indented. */
  scrollClassName?: string;
  /** Override horizontal item spacing (Tailwind gap utility), e.g. `gap-1`. */
  gapClassName?: string;
  onPointerDown?: PointerEventHandler<HTMLElement> | undefined;
  'aria-label'?: string;
  /** Tailwind `from-*` class for gradient edges. Must match the parent's bg. */
  gradientFrom?: string;
  /** Show left/right edge fade overlays for overflow hinting. */
  showEdgeFade?: boolean;
  /** Edge treatment style for overflow hinting. */
  edgeEffect?: 'fade' | 'blur' | 'none';
}

function NavigationList({
  children,
  className,
  scrollClassName,
  gapClassName = 'gap-5',
  onPointerDown,
  'aria-label': ariaLabel,
  gradientFrom = 'from-background-secondary',
  showEdgeFade = true,
  edgeEffect = 'fade',
}: NavigationListProps): React.ReactNode {
  const scrollRef = useRef<HTMLElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const updateGradients = useCallback((): void => {
    const el = scrollRef.current;
    if (el === null) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el === null) return;

    const observer = new ResizeObserver(() => {
      updateGradients();
    });
    observer.observe(el);

    // Initial check after mount
    updateGradients();

    return (): void => {
      observer.disconnect();
    };
  }, [updateGradients]);

  return (
    <div className={cn('relative w-full', className)}>
      {/* Left gradient */}
      {showEdgeFade && edgeEffect === 'fade' ? (
        <div
          aria-hidden="true"
          className={cn(
            `absolute left-0 top-0 bottom-0 z-10 w-8 md:w-16 bg-linear-to-r ${gradientFrom} to-transparent pointer-events-none transition-opacity duration-normal`,
            showLeft ? 'opacity-100' : 'opacity-0',
          )}
        />
      ) : null}
      {showEdgeFade && edgeEffect === 'blur' ? (
        <div
          aria-hidden="true"
          className={cn(
            'absolute left-0 top-0 bottom-0 z-10 w-5 md:w-8 pointer-events-none supports-[backdrop-filter]:backdrop-blur-xs transition-opacity duration-normal',
            '[mask-image:linear-gradient(to_right,black_0%,transparent_100%)]',
            '[-webkit-mask-image:linear-gradient(to_right,black_0%,transparent_100%)]',
            showLeft ? 'opacity-65' : 'opacity-0',
          )}
        />
      ) : null}

      {/* Scroll container */}
      <nav
        ref={scrollRef}
        aria-label={ariaLabel}
        onScroll={updateGradients}
        onPointerDown={onPointerDown}
        className={cn(
          'flex items-center overflow-x-auto overflow-y-hidden overscroll-y-none scrollbar-none',
          gapClassName,
          scrollClassName,
        )}
      >
        {children}
      </nav>

      {/* Right gradient */}
      {showEdgeFade && edgeEffect === 'fade' ? (
        <div
          aria-hidden="true"
          className={cn(
            `absolute right-0 top-0 bottom-0 z-10 w-8 md:w-16 bg-linear-to-l ${gradientFrom} to-transparent pointer-events-none transition-opacity duration-normal`,
            showRight ? 'opacity-100' : 'opacity-0',
          )}
        />
      ) : null}
      {showEdgeFade && edgeEffect === 'blur' ? (
        <div
          aria-hidden="true"
          className={cn(
            'absolute right-0 top-0 bottom-0 z-10 w-5 md:w-8 pointer-events-none supports-[backdrop-filter]:backdrop-blur-xs transition-opacity duration-normal',
            '[mask-image:linear-gradient(to_left,black_0%,transparent_100%)]',
            '[-webkit-mask-image:linear-gradient(to_left,black_0%,transparent_100%)]',
            showRight ? 'opacity-65' : 'opacity-0',
          )}
        />
      ) : null}
    </div>
  );
}

export { NavigationList, NavigationListItem, NavLink };
export type { NavigationListProps, NavigationListItemProps, NavLinkProps };
